import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useReducedMotion } from 'framer-motion';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { apiRequest, queryClient } from '@/lib/queryClient';
import {
  usePipelinePreferences,
  defaultPipelinePreferences,
} from '@/hooks/use-pipeline-preferences';
import { usePipelinePresence } from '@/hooks/use-pipeline-presence';

import type { PipelineContextValue } from './types';
import type {
  PipelineStage,
  Customer,
  PendingStageChange,
  LeadEnquiryQueueResponse,
  LeadEnquiryQueueItem,
  PipelineEnquiryAttention,
} from '../types';
import type { SimpleFilterCriteria } from '@/components/pipeline/SimplePipelineFilters';

import { pipelineFilterDefaults, computeFilterResult, buildCustomerHaystack } from '../utils';
import {
  useCopyToClipboard,
  usePipelineMutations,
  usePipelineModals,
  usePipelineCardExpansion,
  usePipelineScroll,
} from '../hooks';

// Stable empty arrays to avoid creating new references on every lookup miss
// Frozen to prevent accidental mutation (would silently corrupt all consumers)
const EMPTY_CUSTOMERS = Object.freeze([] as Customer[]) as Customer[];
const ASSIGNED_ENQUIRY_PAGE_SIZE = 100;

const ENQUIRY_ATTENTION_PRIORITY = {
  needs_attention: 0,
  waiting: 1,
  new: 2,
} as const;

function getAttentionThreshold(item: LeadEnquiryQueueItem): number {
  const value =
    item.ageState === 'needs_attention'
      ? item.needsAttentionAt
      : item.ageState === 'waiting'
        ? item.waitingAt
        : item.receivedAt;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
}

function isHigherPriorityEnquiry(
  candidate: LeadEnquiryQueueItem,
  current: LeadEnquiryQueueItem
): boolean {
  const priorityDifference =
    ENQUIRY_ATTENTION_PRIORITY[candidate.ageState] - ENQUIRY_ATTENTION_PRIORITY[current.ageState];

  if (priorityDifference !== 0) return priorityDifference < 0;
  return getAttentionThreshold(candidate) < getAttentionThreshold(current);
}

function projectEnquiryAttention(item: LeadEnquiryQueueItem): PipelineEnquiryAttention {
  return {
    enquiryId: item.enquiryId,
    classification: item.classification,
    identityReviewStatus: item.identityReviewStatus,
    identityReviewRequired: item.identityReviewRequired,
    rescued: item.rescued,
    assignee: item.assignee,
    firstContactedAt: item.firstContactedAt,
    receivedAt: item.receivedAt,
    waitingAt: item.waitingAt,
    needsAttentionAt: item.needsAttentionAt,
    ageState: item.ageState,
  };
}

export async function fetchAllAssignedEnquiries(): Promise<LeadEnquiryQueueResponse> {
  const items: LeadEnquiryQueueItem[] = [];
  let offset = 0;
  let total = 0;

  do {
    const page = await apiRequest<LeadEnquiryQueueResponse>(
      'GET',
      `/api/pipeline/enquiries/queue?scope=assigned_to_me&limit=${ASSIGNED_ENQUIRY_PAGE_SIZE}&offset=${offset}`
    );
    total = page.data.total;
    items.push(...page.data.items);
    if (page.data.items.length === 0 && items.length < total) {
      throw new Error('Assigned enquiry pagination stopped before all rows were returned');
    }
    offset += page.data.items.length;
  } while (offset < total);

  return {
    success: true,
    data: {
      items,
      total,
      limit: ASSIGNED_ENQUIRY_PAGE_SIZE,
      offset: 0,
    },
  };
}

// Create context with undefined default
const PipelineContext = createContext<PipelineContextValue | undefined>(undefined);

export interface PipelineProviderProps {
  children: React.ReactNode;
}

/**
 * Pipeline Provider - composes all hooks and provides unified context
 */
export function PipelineProvider({ children }: PipelineProviderProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const prefersReducedMotion = useReducedMotion();

  // Memoize filter defaults
  const pipelinePreferencesDefaults = useMemo(
    () => ({
      ...defaultPipelinePreferences,
      filters: pipelineFilterDefaults,
    }),
    []
  );

  // Preferences
  const { preferences, updatePreferences } = usePipelinePreferences(pipelinePreferencesDefaults);

  // Search query (debounced value set from PipelineHeader)
  const [searchQuery, setSearchQuery] = useState('');

  // View mode
  const viewMode = preferences.viewMode ?? 'pipeline';
  const setViewMode = useCallback(
    (mode: 'pipeline' | 'calendar') => {
      updatePreferences((prev) => ({ ...prev, viewMode: mode }));
    },
    [updatePreferences]
  );

  // Layout
  const pipelineLayout = preferences.layout ?? 'standard';
  const setPipelineLayout = useCallback(
    (layout: 'standard' | 'compact') => {
      updatePreferences((prev) => ({ ...prev, layout }));
    },
    [updatePreferences]
  );

  // Focus matches only
  const focusMatchesOnly = preferences.focusMatchesOnly ?? true;
  const toggleFocusMatchesOnly = useCallback(() => {
    updatePreferences((prev) => ({
      ...prev,
      focusMatchesOnly: !prev.focusMatchesOnly,
    }));
  }, [updatePreferences]);

  // Collapsed stages
  const collapsedStages = useMemo(
    () => new Set<number>(preferences.collapsedStageIds ?? []),
    [preferences.collapsedStageIds]
  );

  const updateCollapsedStages = useCallback(
    (updater: (current: Set<number>) => Set<number>) => {
      updatePreferences((prev) => {
        const nextSet = updater(new Set(prev.collapsedStageIds ?? []));
        return {
          ...prev,
          collapsedStageIds: Array.from(nextSet),
        };
      });
    },
    [updatePreferences]
  );

  // Filters
  const filters = useMemo<SimpleFilterCriteria>(
    () => ({
      ...pipelineFilterDefaults,
      ...(preferences.filters as SimpleFilterCriteria | undefined),
    }),
    [preferences.filters]
  );

  // Filtering state - used to temporarily disable animations during filter transitions
  const [isFiltering, setIsFiltering] = useState(false);

  const setFilters = useCallback(
    (nextFilters: SimpleFilterCriteria) => {
      setIsFiltering(true);
      updatePreferences((prev) => ({
        ...prev,
        filters: nextFilters,
      }));
      // Clear filtering state after a short delay to allow re-renders to complete
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsFiltering(false));
      });
    },
    [updatePreferences]
  );

  // Fetch pipeline data
  const {
    data: pipelineData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['/api/pipeline-stages/data'],
    queryFn: () =>
      apiRequest<{
        stages: PipelineStage[];
        customers: Customer[];
        stageCustomerCounts: Record<number, number>;
        stageTotalValues: Record<number, number>;
      }>('GET', '/api/pipeline-stages/data'),
    staleTime: 60000,
    gcTime: 300000,
  });

  // Assignment is enquiry-level. Fetch the current user's open enquiries
  // independently from the customer pipeline so claiming never implies contact.
  const { data: assignedEnquiryData } = useQuery<LeadEnquiryQueueResponse>({
    queryKey: ['/api/pipeline/enquiries/queue', { scope: 'assigned_to_me' }],
    queryFn: fetchAllAssignedEnquiries,
    enabled: Boolean(user),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  // Extract data
  let extractedCustomers: Customer[] = [];
  if (pipelineData?.customers) {
    if (Array.isArray(pipelineData.customers)) {
      extractedCustomers = pipelineData.customers;
    } else if (
      pipelineData.customers &&
      typeof pipelineData.customers === 'object' &&
      'data' in pipelineData.customers
    ) {
      extractedCustomers = Array.isArray((pipelineData.customers as any).data)
        ? (pipelineData.customers as any).data
        : [];
    }
  }

  const stages = Array.isArray(pipelineData?.stages) ? pipelineData.stages : [];
  const rawCustomers: Customer[] = Array.isArray(extractedCustomers) ? extractedCustomers : [];
  const stageCustomerCounts = pipelineData?.stageCustomerCounts || {};
  const stageTotalValues = pipelineData?.stageTotalValues || {};

  const assignedAttentionByCustomerId = useMemo(() => {
    const selectedItems = new Map<number, LeadEnquiryQueueItem>();

    for (const item of assignedEnquiryData?.data.items ?? []) {
      if (item.customerId === null) continue;
      const current = selectedItems.get(item.customerId);
      if (!current || isHigherPriorityEnquiry(item, current)) {
        selectedItems.set(item.customerId, item);
      }
    }

    const projected = new Map<number, PipelineEnquiryAttention>();
    for (const [customerId, item] of selectedItems) {
      projected.set(customerId, projectEnquiryAttention(item));
    }
    return projected;
  }, [assignedEnquiryData?.data.items]);

  // Pre-compute timestamps and search haystacks once when data loads
  // Avoids expensive Date parsing during filter/sort and string building during search
  const customers = useMemo(() => {
    return rawCustomers.map((customer) => {
      const enriched: Customer = {
        ...customer,
        createdAtTimestamp: customer.createdAt ? new Date(customer.createdAt).getTime() : 0,
        lastContactTimestamp: customer.lastContactDate
          ? new Date(customer.lastContactDate).getTime()
          : 0,
        pipelineUpdatedAtTimestamp: customer.pipelineUpdatedAt
          ? new Date(customer.pipelineUpdatedAt).getTime()
          : 0,
        nextContactTimestamp: customer.nextContactDate
          ? new Date(customer.nextContactDate).getTime()
          : Infinity,
        enquiryAttention: assignedAttentionByCustomerId.get(customer.id),
      };
      // Pre-build search haystack (everything except stage name, which is dynamic)
      enriched._searchHaystack = buildCustomerHaystack(enriched);
      return enriched;
    });
  }, [rawCustomers, assignedAttentionByCustomerId]);

  // Stage maps - depend on stages array to ensure fresh references
  const stageNameById = useMemo(() => {
    const map = new Map<number, string>();
    stages.forEach((stage) => map.set(stage.id, stage.name));
    return map;
  }, [stages]);

  const stageById = useMemo(() => {
    const map = new Map<number, PipelineStage>();
    stages.forEach((stage) => map.set(stage.id, stage));
    return map;
  }, [stages]);

  // Compose hooks
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const mutations = usePipelineMutations();
  const modals = usePipelineModals();
  const cardExpansion = usePipelineCardExpansion();

  // Scroll management
  const stageScrollPositions = preferences.scrollPositions ?? {};
  const updateScrollPosition = useCallback(
    (stageId: number, position: number) => {
      updatePreferences((prev) => ({
        ...prev,
        scrollPositions: {
          ...(prev.scrollPositions || {}),
          [stageId]: position,
        },
      }));
    },
    [updatePreferences]
  );

  const scroll = usePipelineScroll({
    stageScrollPositions,
    updateScrollPosition,
    prefersReducedMotion: prefersReducedMotion ?? false,
  });

  // Presence
  const { presence: pipelinePresence, updateCardPresence, currentUserId } = usePipelinePresence();
  const activePresenceUsers = pipelinePresence.users ?? [];
  const otherActiveUsers = useMemo(
    () =>
      currentUserId
        ? activePresenceUsers.filter((person) => person.userId !== currentUserId)
        : activePresenceUsers,
    [activePresenceUsers, currentUserId]
  );

  // Local notes state
  const [localNotes, setLocalNotesState] = useState<Record<number, string>>({});
  const setLocalNotes = useCallback((customerId: number, notes: string) => {
    setLocalNotesState((prev) => ({ ...prev, [customerId]: notes }));
  }, []);

  // Call state
  const [callInProgress, setCallInProgress] = useState(false);

  // Detail sheet state (for horizontal kanban layout)
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);

  // Outcomes row collapse state - collapsed by default to give main pipeline stages more space
  const [outcomesCollapsed, setOutcomesCollapsed] = useState(true);

  // Detail sheet actions
  const openCustomerDetail = useCallback((customer: Customer) => {
    setSelectedCustomerId(customer.id);
    setDetailSheetOpen(true);
  }, []);

  const closeCustomerDetail = useCallback(() => {
    setDetailSheetOpen(false);
    // Delay clearing the customer ID to allow close animation
    setTimeout(() => setSelectedCustomerId(null), 300);
  }, []);

  const toggleOutcomesCollapsed = useCallback(() => {
    setOutcomesCollapsed((prev) => !prev);
  }, []);

  // Compute filter result
  const filterResult = useMemo(
    () =>
      computeFilterResult({
        customers,
        filters,
        searchQuery,
        focusMatchesOnly,
        stageNameById,
      }),
    [customers, filters, focusMatchesOnly, searchQuery, stageNameById]
  );

  const filteredCustomers = filterResult.filteredCustomers;
  const matchingCustomerIds = filterResult.matchingIds;
  const searchActive = filterResult.searchActive;
  const highlightTerms = filterResult.highlightTerms;

  // Pre-build stage→customers map in one pass instead of O(N) filter per stage
  const customersByStageMap = useMemo(() => {
    const map = new Map<number, Customer[]>();
    for (const customer of filteredCustomers) {
      const stageId = customer.pipelineStageId;
      if (stageId != null) {
        let arr = map.get(stageId);
        if (!arr) {
          arr = [];
          map.set(stageId, arr);
        }
        arr.push(customer);
      }
    }
    return map;
  }, [filteredCustomers]);

  const getCustomersByStage = useCallback(
    (stageId: number) => customersByStageMap.get(stageId) ?? EMPTY_CUSTOMERS,
    [customersByStageMap]
  );

  // Single-pass computation of stageMatchCounts, globalSearchResults, stageSummaries, and pipelineStats
  // Previously these were 4 separate useMemos each iterating filteredCustomers independently
  const { stageMatchCounts, globalSearchResults, stageSummaries, pipelineStats } = useMemo(() => {
    const matchCountMap = new Map<number, number>();
    const stageCustomerCountMap = new Map<number, number>();
    const stageValueMap = new Map<number, number>();
    const searchResults: Customer[] = [];
    let totalValue = 0;

    // Single pass over filteredCustomers
    for (const customer of filteredCustomers) {
      const stageId = customer.pipelineStageId;
      const dealVal = customer.dealValue || 0;
      totalValue += dealVal;

      if (stageId) {
        stageCustomerCountMap.set(stageId, (stageCustomerCountMap.get(stageId) ?? 0) + 1);
        stageValueMap.set(stageId, (stageValueMap.get(stageId) ?? 0) + dealVal);

        if (matchingCustomerIds.has(customer.id)) {
          matchCountMap.set(stageId, (matchCountMap.get(stageId) ?? 0) + 1);
        }
      }

      if (matchingCustomerIds.has(customer.id)) {
        searchResults.push(customer);
      }
    }

    const totalDeals = filteredCustomers.length;

    // Derive stageSummaries and pipelineStats from the maps
    const summaries = stages.map((stage) => {
      const total = stageCustomerCountMap.get(stage.id) ?? 0;
      const matches = matchCountMap.get(stage.id) ?? 0;
      return { stage, total, matches };
    });

    const stageProgress = stages.map((stage) => {
      const count = stageCustomerCountMap.get(stage.id) ?? 0;
      const stageValue = (stageValueMap.get(stage.id) ?? 0) / 100;
      const percentage = totalDeals > 0 ? Math.round((count / totalDeals) * 100) : 0;

      return {
        stageId: stage.id,
        stageName: stage.name,
        stageColor: stage.color,
        count,
        value: stageValue,
        percentage,
      };
    });

    return {
      stageMatchCounts: matchCountMap,
      globalSearchResults: searchResults,
      stageSummaries: summaries,
      pipelineStats: { totalDeals, totalValue: totalValue / 100, stageProgress },
    };
  }, [filteredCustomers, matchingCustomerIds, stages]);

  const totalMatches = matchingCustomerIds.size;

  // Stage announcement for accessibility
  const [stageAnnouncement, setStageAnnouncement] = useState('');

  const announceStageState = useCallback(
    (stage: PipelineStage, willCollapse: boolean, count: number) => {
      const label = willCollapse ? 'collapsed' : 'expanded';
      setStageAnnouncement(
        `Stage "${stage.name}" ${label}. ${count} ${count === 1 ? 'deal' : 'deals'}.`
      );
    },
    []
  );

  useEffect(() => {
    if (!stageAnnouncement) return;
    const timeout = window.setTimeout(() => setStageAnnouncement(''), 2000);
    return () => window.clearTimeout(timeout);
  }, [stageAnnouncement]);

  // Toggle stage collapse
  const toggleStageCollapse = useCallback(
    (stage: PipelineStage, stageCustomerCount: number) => {
      const willCollapse = !collapsedStages.has(stage.id);
      updateCollapsedStages((current) => {
        const next = new Set(current);
        if (willCollapse) {
          next.add(stage.id);
        } else {
          next.delete(stage.id);
        }
        return next;
      });
      announceStageState(stage, willCollapse, stageCustomerCount);
    },
    [announceStageState, collapsedStages, updateCollapsedStages]
  );

  // Drag and drop state
  const [draggedCustomer, setDraggedCustomer] = useState<Customer | null>(null);

  const onDragStart = useCallback((customer: Customer) => {
    setDraggedCustomer(customer);
  }, []);

  const onDragEnd = useCallback(() => {
    setDraggedCustomer(null);
  }, []);

  // Safety net: if a dragged card unmounts mid-drag (collab update, optimistic
  // mutation, filter change), the card's own dragend may never fire. Listen on
  // window so we always clear draggedCustomer and don't wedge the board.
  useEffect(() => {
    const clearDrag = () => setDraggedCustomer(null);
    window.addEventListener('dragend', clearDrag);
    window.addEventListener('drop', clearDrag);
    return () => {
      window.removeEventListener('dragend', clearDrag);
      window.removeEventListener('drop', clearDrag);
    };
  }, []);

  const onDrop = useCallback(
    (stage: PipelineStage) => {
      if (!draggedCustomer || draggedCustomer.pipelineStageId === stage.id) return;

      const stageName = stage.name.toLowerCase();

      if (stageName === 'won') {
        modals.openWonModal({
          customerId: draggedCustomer.id,
          customer: draggedCustomer,
          toStageId: stage.id,
          stageName: stage.name,
        });
        return;
      }

      if (stageName === 'installation completed') {
        modals.openInstallationModal({
          customerId: draggedCustomer.id,
          customer: draggedCustomer,
          toStageId: stage.id,
          stageName: stage.name,
        });
        return;
      }

      mutations.updateCustomerMutation.mutate({
        id: draggedCustomer.id,
        updates: {
          pipelineStageId: stage.id,
          pipelineNotes: draggedCustomer.pipelineNotes || null,
        },
      });

      toast({
        title: 'Customer moved',
        description: `${draggedCustomer.name} moved to ${stage.name}`,
      });
    },
    [draggedCustomer, modals, mutations.updateCustomerMutation, toast]
  );

  const onTrashDrop = useCallback(() => {
    if (!draggedCustomer) return;

    mutations.updateCustomerMutation.mutate({
      id: draggedCustomer.id,
      updates: {
        pipelineStageId: null,
        pipelineNotes: draggedCustomer.pipelineNotes,
      },
    });

    toast({
      title: 'Customer removed',
      description: `${draggedCustomer.name} removed from pipeline`,
    });
  }, [draggedCustomer, mutations.updateCustomerMutation, toast]);

  // Initiate call
  const initiateCall = useCallback(
    async (customerId: number, phoneNumber: string | null) => {
      if (!phoneNumber) {
        toast({
          title: 'Error',
          description: 'No phone number available for this customer',
          variant: 'destructive',
        });
        return;
      }

      try {
        setCallInProgress(true);
        await apiRequest('POST', '/api/twilio/call', {
          customerId,
          customerPhone: phoneNumber,
        });

        toast({
          title: 'Call initiated',
          description: 'The call is being connected...',
        });

        setTimeout(() => setCallInProgress(false), 3000);
      } catch (err) {
        console.error('Error initiating call:', err);
        toast({
          title: 'Call failed',
          description: 'Could not initiate the call. Please try again.',
          variant: 'destructive',
        });
        setCallInProgress(false);
      }
    },
    [toast]
  );

  // Select customer from search
  const selectCustomerFromSearch = useCallback(
    (customer: Customer) => {
      cardExpansion.skipAutoScrollRef.current = true;

      if (customer.pipelineStageId) {
        scroll.scrollStageIntoView(customer.pipelineStageId);
      }

      const node = scroll.refs.customerCardRefs.current.get(customer.id);
      if (node) {
        node.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'center',
        });
      }

      cardExpansion.setOpenCardId(customer.id);
      cardExpansion.setHighlightedCustomerId(customer.id);

      setTimeout(() => {
        cardExpansion.skipAutoScrollRef.current = false;
      }, 500);
    },
    [cardExpansion, prefersReducedMotion, scroll]
  );

  // Update presence when card changes
  const lastPresence = useRef({ cardId: null as number | null, stageId: null as number | null });
  useEffect(() => {
    if (!updateCardPresence) return;

    const active = customers.find((c) => c.id === cardExpansion.openCardId);
    const stageId = active?.pipelineStageId ?? null;

    if (viewMode !== 'pipeline' || cardExpansion.openCardId === null) {
      if (lastPresence.current.cardId !== null || lastPresence.current.stageId !== null) {
        updateCardPresence(null, null);
        lastPresence.current = { cardId: null, stageId: null };
      }
      return;
    }

    const next = { cardId: cardExpansion.openCardId, stageId };
    if (
      lastPresence.current.cardId !== next.cardId ||
      lastPresence.current.stageId !== next.stageId
    ) {
      updateCardPresence(next.cardId, next.stageId);
      lastPresence.current = next;
    }
  }, [cardExpansion.openCardId, viewMode, customers, updateCardPresence]);

  // Cleanup presence on unmount
  useEffect(() => {
    return () => {
      updateCardPresence(null, null);
    };
  }, [updateCardPresence]);

  // Build context value
  const contextValue = useMemo<PipelineContextValue>(
    () => ({
      state: {
        stages,
        customers,
        stageCustomerCounts,
        stageTotalValues,
        isLoading,
        error: error as Error | null,
        viewMode,
        pipelineLayout,
        collapsedStages,
        openCardId: cardExpansion.openCardId,
        highlightedCustomerId: cardExpansion.highlightedCustomerId,
        highlightedTaskId: cardExpansion.highlightedTaskId,
        filters,
        searchQuery,
        focusMatchesOnly,
        isFiltering,
        draggedCustomer,
        wonModalOpen: modals.wonModalOpen,
        installationModalOpen: modals.installationModalOpen,
        pendingStageChange: modals.pendingStageChange,
        bookingPopupOpen: modals.bookingPopupOpen,
        bookingCustomer: modals.bookingCustomer,
        customerFormOpen: modals.customerFormOpen,
        localNotes,
        callInProgress,
        detailSheetOpen,
        selectedCustomerId,
        outcomesCollapsed,
      },
      actions: {
        setViewMode,
        setPipelineLayout,
        toggleStageCollapse,
        expandCard: cardExpansion.expandCard,
        collapseCard: cardExpansion.collapseCard,
        toggleCard: cardExpansion.toggleCard,
        setHighlightedCustomerId: cardExpansion.setHighlightedCustomerId,
        setHighlightedTaskId: cardExpansion.setHighlightedTaskId,
        setFilters,
        setSearchQuery,
        toggleFocusMatchesOnly,
        onDragStart,
        onDragEnd,
        onDrop,
        onTrashDrop,
        openWonModal: modals.openWonModal,
        openInstallationModal: modals.openInstallationModal,
        closeModals: modals.closeModals,
        openBookingPopup: modals.openBookingPopup,
        closeBookingPopup: modals.closeBookingPopup,
        setCustomerFormOpen: modals.setCustomerFormOpen,
        updateCustomer: (id, updates) => mutations.updateCustomerMutation.mutate({ id, updates }),
        deleteCustomer: (customerId) => mutations.deleteCustomerMutation.mutate(customerId),
        updateLeadScore: mutations.updateLeadScore,
        createOpenSolarProject: (customerId) =>
          mutations.createOpenSolarProjectMutation.mutate(customerId),
        setLocalNotes,
        initiateCall,
        selectCustomerFromSearch,
        scrollStageIntoView: scroll.scrollStageIntoView,
        openCustomerDetail,
        closeCustomerDetail,
        toggleOutcomesCollapsed,
      },
      meta: {
        filteredCustomers,
        matchingCustomerIds,
        searchActive,
        highlightTerms,
        stageMatchCounts,
        totalMatches,
        globalSearchResults,
        stageSummaries,
        stageNameById,
        stageById,
        getCustomersByStage,
        pipelineStats,
        refs: scroll.refs,
        getStageContainerRef: scroll.getStageContainerRef,
        handleStageScroll: scroll.handleStageScroll,
        isCopied,
        copyToClipboard,
        otherActiveUsers,
        isUpdating: mutations.updateCustomerMutation.isPending,
        isDeleting: mutations.deleteCustomerMutation.isPending,
      },
    }),
    [
      stages,
      customers,
      stageCustomerCounts,
      stageTotalValues,
      isLoading,
      error,
      viewMode,
      pipelineLayout,
      collapsedStages,
      cardExpansion,
      filters,
      searchQuery,
      focusMatchesOnly,
      isFiltering,
      draggedCustomer,
      modals,
      localNotes,
      callInProgress,
      detailSheetOpen,
      selectedCustomerId,
      outcomesCollapsed,
      setViewMode,
      setPipelineLayout,
      toggleStageCollapse,
      setFilters,
      setSearchQuery,
      toggleFocusMatchesOnly,
      onDragStart,
      onDragEnd,
      onDrop,
      onTrashDrop,
      mutations,
      setLocalNotes,
      initiateCall,
      selectCustomerFromSearch,
      scroll,
      openCustomerDetail,
      closeCustomerDetail,
      toggleOutcomesCollapsed,
      filteredCustomers,
      matchingCustomerIds,
      searchActive,
      highlightTerms,
      stageMatchCounts,
      totalMatches,
      globalSearchResults,
      stageSummaries,
      stageNameById,
      stageById,
      getCustomersByStage,
      pipelineStats,
      isCopied,
      copyToClipboard,
      otherActiveUsers,
    ]
  );

  return <PipelineContext.Provider value={contextValue}>{children}</PipelineContext.Provider>;
}

/**
 * Hook to access pipeline context
 * Throws if used outside PipelineProvider
 */
export function usePipelineContext(): PipelineContextValue {
  const context = useContext(PipelineContext);
  if (context === undefined) {
    // During Vite HMR, context may be temporarily unavailable.
    // Return a safe loading-state default instead of crashing the app.
    console.warn(
      '[usePipelineContext] PipelineProvider not found - returning defaults (likely HMR)'
    );
    const noop = () => {};
    return {
      state: {
        stages: [],
        customers: [],
        isLoading: true,
        error: null,
        viewMode: 'board',
        filters: {
          searchQuery: '',
          leadScoreFilter: null,
          recentLeadsFilter: null,
          contactStatusFilter: null,
          sourceFilter: null,
        },
        searchQuery: '',
        focusMatchesOnly: true,
        wonModalOpen: false,
        installationModalOpen: false,
        pendingStageChange: null,
        bookingPopupOpen: false,
        bookingCustomer: null,
        detailSheetOpen: false,
        selectedCustomerId: null,
        highlightedCustomerId: null,
        isFiltering: false,
        outcomesCollapsed: false,
        pipelineLayout: 'standard',
        collapsedStages: new Set(),
      } as any,
      actions: new Proxy({} as any, { get: () => noop }),
      meta: new Proxy({} as any, {
        get: (_, prop) => {
          if (prop === 'matchingCustomerIds') return new Set();
          if (
            prop === 'filteredCustomers' ||
            prop === 'globalSearchResults' ||
            prop === 'otherActiveUsers'
          )
            return [];
          if (prop === 'highlightTerms') return [];
          if (prop === 'stageSummaries' || prop === 'stageById') return new Map();
          if (prop === 'isCopied') return {};
          return 0;
        },
      }),
    };
  }
  return context;
}

/**
 * Safe hook that returns undefined if context is not available
 * Use this in components that may render in portals or during HMR
 * where context availability cannot be guaranteed
 */
export function usePipelineContextSafe(): PipelineContextValue | undefined {
  return useContext(PipelineContext);
}

/**
 * Hook to access only pipeline state
 */
export function usePipelineState() {
  return usePipelineContext().state;
}

/**
 * Hook to access only pipeline actions
 */
export function usePipelineActions() {
  return usePipelineContext().actions;
}

/**
 * Hook to access only pipeline meta/computed values
 */
export function usePipelineMeta() {
  return usePipelineContext().meta;
}
